// CompletionRing: how much of something is filled in, as arcs on a small ring. The accent arc is
// filled, the green arc is locked (signed off), drawn over the accent so a field locked blank on
// purpose still reads done. The arcs spring to their length, and the ring flares with accent
// light once when it closes. docs/operations/design-standard.md, "Controls".

import React, { useEffect, useRef, useState } from 'react';
import { ringLabel } from './ui/ProgressRing';

export interface CompletionRingProps {
  filled: number;
  total: number;
  locked?: number;
  size?: number;
  /** Needs someone now (airspace needing action): the base ring turns red, breathes, and shows "!". */
  attention?: boolean;
}

const ARC = 'transition-[stroke-dashoffset,opacity] duration-spring ease-spring motion-reduce:transition-[opacity] motion-reduce:duration-quick';

const FLARE_MS = 600;

const CompletionRing: React.FC<CompletionRingProps> = ({ filled, total, locked = 0, size = 18, attention = false }) => {
  const clamp = (n: number) => (total > 0 ? Math.min(1, Math.max(0, n / total)) : 0);
  const fillPct = clamp(filled);
  const lockPct = clamp(locked);
  const c = size / 2;
  const r = (size - 3) / 2;
  const circumference = 2 * Math.PI * r;
  const done = total > 0 && (filled >= total || locked >= total);

  // Only a close seen happening flares; a ring that loads complete is just complete.
  const was = useRef(done);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    if (done && !was.current) setLit(true);
    was.current = done;
  }, [done]);
  useEffect(() => {
    if (!lit) return undefined;
    const t = window.setTimeout(() => setLit(false), FLARE_MS);
    return () => window.clearTimeout(t);
  }, [lit]);

  // At zero the arc is hidden, not unmounted: a round cap on a zero-length dash still draws a dot.
  const arc = (pct: number, tone: string) => (
    <circle
      cx={c} cy={c} r={r}
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray={circumference}
      strokeDashoffset={circumference - pct * circumference}
      transform={`rotate(-90 ${c} ${c})`}
      className={`${tone} ${ARC}`}
      style={{ opacity: pct > 0 ? 1 : 0 }}
    />
  );

  const u = size / 18;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`flex-shrink-0 overflow-visible transition-[filter] duration-standard ease-emphasized ${lit ? 'drop-shadow-glow' : ''}`}
      role="img"
      aria-label={ringLabel(filled, total, locked, attention)}
    >
      <circle
        cx={c} cy={c} r={r}
        fill="none"
        strokeWidth="2"
        className={`transition-colors duration-standard ease-emphasized ${attention ? 'stroke-red-400 motion-safe:animate-pulse' : 'stroke-outline-variant'}`}
      />
      {arc(fillPct, 'stroke-primary')}
      {arc(lockPct, 'stroke-emerald-400')}
      <g className={`fill-red-400 transition-opacity duration-standard ease-emphasized ${attention ? 'opacity-100' : 'opacity-0'}`}>
        <rect x={c - 0.8 * u} y={c - 3.6 * u} width={1.6 * u} height={4.4 * u} rx={0.8 * u} />
        <circle cx={c} cy={c + 2.7 * u} r={0.95 * u} />
      </g>
    </svg>
  );
};

export default CompletionRing;
